'use client'

import { NotificationChannel, NotificationType } from '@/lib/services/notifications/types'
import {
  Modal
} from '@heroui/react'
import { Button } from '@/components/ui/button'
import { Input } from '@/components/ui/form-elements/Input'
import { CompatSelect as Select, ListBoxItem } from '@/components/ui/compat-select'
import { showToast } from '@/lib/utils/toast'
import { useState } from 'react'

interface DebugNotificationProps {
  isOpen: boolean
  onClose: () => void
}

export function DebugNotification({ isOpen, onClose }: DebugNotificationProps) {
  const [title, setTitle] = useState('Test Notification')
  const [message, setMessage] = useState('This is a test notification')
  const [type, setType] = useState<NotificationType>('SYSTEM')
  const [priority, setPriority] = useState(1)
  const [isLoading, setIsLoading] = useState(false)

  const handleSend = async () => {
    try {
      setIsLoading(true)
      console.log('Sending debug notification:', {
        title,
        message,
        type,
        priority,
        metadata: {
          priority,
          immediate: true,
          debug: true
        },
        channels: [NotificationChannel.WEBSOCKET]
      })

      const response = await fetch('/api/debug/send-notification', {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json'
        },
        body: JSON.stringify({
          title,
          message,
          type,
          metadata: {
            priority,
            immediate: true,
            debug: true
          },
          channels: [NotificationChannel.WEBSOCKET]
        })
      })

      console.log('Debug notification API response:', {
        status: response.status,
        ok: response.ok,
        statusText: response.statusText
      })

      const data = await response.json()
      console.log('Debug notification API data:', data)

      if (!response.ok) {
        throw new Error(data.error || 'Failed to send notification')
      }

      console.log('Debug notification sent successfully:', data)
      showToast({
        title: 'Notification Sent',
        description: 'Debug notification has been sent to all users',
        color: 'success',
        timeout: 3000
      })

      onClose()
    } catch (error) {
      console.error('Error sending debug notification:', error)
      showToast({
        title: 'Failed to send notification',
        description: error instanceof Error ? error.message : 'Failed to send notification',
        color: 'danger',
        timeout: 5000
      })
    } finally {
      setIsLoading(false)
    }
  }

  return (
    <Modal.Backdrop
      isOpen={isOpen}
      onOpenChange={(open) => {
        if (!open) onClose()
      }}
    >
      <Modal.Container placement='top'>
        <Modal.Dialog>
          <Modal.Header>Send Debug Notification</Modal.Header>
          <Modal.Body>
            <div className='flex flex-col gap-4 text-foreground'>
              <Input label='Title' value={title} onChange={(e) => setTitle(e.target.value)} />
              <Input label='Message' value={message} onChange={(e) => setMessage(e.target.value)} />
              <Select
                label='Type'
                selectedKeys={[type]}
                onSelectionChange={(keys) => setType(Array.from(keys)[0] as NotificationType)}
              >
                <ListBoxItem key='SYSTEM'>System</ListBoxItem>
                <ListBoxItem key='INTERACTION_FAVOURITE'>Favorite</ListBoxItem>
                <ListBoxItem key='INTERACTION_COMMENT'>Comment</ListBoxItem>
                <ListBoxItem key='INTERACTION_RATING'>Rating</ListBoxItem>
                <ListBoxItem key='INTERACTION_SHARE'>Share</ListBoxItem>
              </Select>
              <Select
                label='Priority'
                selectedKeys={[priority.toString()]}
                onSelectionChange={(keys) => setPriority(parseInt(Array.from(keys)[0] as string))}
              >
                <ListBoxItem key='1'>Low</ListBoxItem>
                <ListBoxItem key='2'>Medium</ListBoxItem>
                <ListBoxItem key='3'>High</ListBoxItem>
              </Select>
            </div>
          </Modal.Body>
          <Modal.Footer>
            <Button variant='ghost' onPress={onClose}>
              Cancel
            </Button>
            <Button variant='primary' onPress={handleSend} isLoading={isLoading}>
              Send
            </Button>
          </Modal.Footer>
        </Modal.Dialog>
      </Modal.Container>
    </Modal.Backdrop>
  )
}
